import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum, boolean, jsonb, check } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { deals } from "./schema-deals.js";
import { accounts, contacts } from "./schema-crm.js";
import { baFinancialPeriods } from "./schema-business-analytics.js";
import { accountCommitments } from "./schema-financial.js";

// ============================================================================
// DEAL FINANCIAL ANALYSIS MODULE - SPRINT 15
// ============================================================================
// Tables: 8 (deal_financial_entities, deal_financial_guarantors, deal_period_selections,
//   deal_intercompany_eliminations, deal_income_inclusion, deal_commitment_inclusion,
//   deal_consolidated_snapshot, hem_benchmarks)
// Purpose: Deal-level financial analysis, consolidated financials, serviceability
// ============================================================================

// ============================================================================
// ENUMS
// ============================================================================

export const entityRoleEnum = pgEnum("crm_x_entity_role_enum", [
  "primary_borrower", "co_borrower", "guarantor_entity", "related_entity"
]);

export const guarantorTreatmentEnum = pgEnum("crm_x_guarantor_treatment_enum", [
  "income_only", "debt_only", "net_contribution", "full_inclusion", "excluded"
]);

export const intercompanyFlowTypeEnum = pgEnum("crm_x_intercompany_flow_type_enum", [
  "management_fee", "rent", "interest", "dividend", "loan_repayment", "service_fee", "other"
]);

export const dealIncomeSourceTypeEnum = pgEnum("crm_x_deal_income_source_type_enum", [
  "entity_ebitda", "guarantor_income", "rental_income", "dividend_income",
  "trust_distribution", "other_income"
]);

// ============================================================================
// TABLE 1: Deal Financial Entities
// ============================================================================

export const dealFinancialEntities = pgTable("crm_x_deal_financial_entities", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  entityRole: entityRoleEnum("entity_role").notNull().default("primary_borrower"),
  includeInAnalysis: boolean("include_in_analysis").notNull().default(true),
  displayOrder: integer("display_order").default(0),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const insertDealFinancialEntitySchema = createInsertSchema(dealFinancialEntities, {
  dealId: z.string().uuid(),
  accountId: z.string().uuid(),
  entityRole: z.enum(["primary_borrower", "co_borrower", "guarantor_entity", "related_entity"]).default("primary_borrower"),
  includeInAnalysis: z.boolean().default(true),
  displayOrder: z.number().int().nullable().optional(),
  notes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });

export const updateDealFinancialEntitySchema = insertDealFinancialEntitySchema
  .omit({ dealId: true, accountId: true }).partial().strict();
export const selectDealFinancialEntitySchema = createSelectSchema(dealFinancialEntities);
export type DealFinancialEntity = typeof dealFinancialEntities.$inferSelect;
export type InsertDealFinancialEntity = z.infer<typeof insertDealFinancialEntitySchema>;

// ============================================================================
// TABLE 2: Deal Financial Guarantors
// ============================================================================

export const dealFinancialGuarantors = pgTable("crm_x_deal_financial_guarantors", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }).notNull(),
  treatment: guarantorTreatmentEnum("treatment").notNull().default("net_contribution"),
  includeInAnalysis: boolean("include_in_analysis").notNull().default(true),
  incomeContributionPercent: numeric("income_contribution_percent", { precision: 5, scale: 2 }).default("100"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const insertDealFinancialGuarantorSchema = createInsertSchema(dealFinancialGuarantors, {
  dealId: z.string().uuid(),
  contactId: z.string().uuid(),
  treatment: z.enum(["income_only", "debt_only", "net_contribution", "full_inclusion", "excluded"]).default("net_contribution"),
  includeInAnalysis: z.boolean().default(true),
  incomeContributionPercent: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });

export const updateDealFinancialGuarantorSchema = insertDealFinancialGuarantorSchema
  .omit({ dealId: true, contactId: true }).partial().strict();
export const selectDealFinancialGuarantorSchema = createSelectSchema(dealFinancialGuarantors);
export type DealFinancialGuarantor = typeof dealFinancialGuarantors.$inferSelect;
export type InsertDealFinancialGuarantor = z.infer<typeof insertDealFinancialGuarantorSchema>;

// ============================================================================
// TABLE 3: Deal Period Selections
// ============================================================================

export const dealPeriodSelections = pgTable("crm_x_deal_period_selections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  financialPeriodId: uuid("financial_period_id").references(() => baFinancialPeriods.id, { onDelete: "cascade" }).notNull(),
  includeInAnalysis: boolean("include_in_analysis").notNull().default(true),
  isPrimaryPeriod: boolean("is_primary_period").default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const insertDealPeriodSelectionSchema = createInsertSchema(dealPeriodSelections, {
  dealId: z.string().uuid(),
  financialPeriodId: z.string().uuid(),
  includeInAnalysis: z.boolean().default(true),
  isPrimaryPeriod: z.boolean().default(false),
}).omit({ id: true, createdAt: true });

export const updateDealPeriodSelectionSchema = insertDealPeriodSelectionSchema.partial();
export const selectDealPeriodSelectionSchema = createSelectSchema(dealPeriodSelections);
export type DealPeriodSelection = typeof dealPeriodSelections.$inferSelect;
export type InsertDealPeriodSelection = z.infer<typeof insertDealPeriodSelectionSchema>;

// ============================================================================
// TABLE 4: Deal Intercompany Eliminations
// ============================================================================

export const dealIntercompanyEliminations = pgTable("crm_x_deal_intercompany_eliminations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  fromAccountId: uuid("from_account_id").references(() => accounts.id).notNull(),
  toAccountId: uuid("to_account_id").references(() => accounts.id).notNull(),
  flowType: intercompanyFlowTypeEnum("flow_type").notNull(),
  description: text("description"),
  annualAmount: numeric("annual_amount", { precision: 15, scale: 2 }).notNull(),
  periodLabel: text("period_label"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdBy: uuid("created_by"),
});

export const insertDealIntercompanyEliminationSchema = createInsertSchema(dealIntercompanyEliminations, {
  dealId: z.string().uuid(),
  fromAccountId: z.string().uuid(),
  toAccountId: z.string().uuid(),
  flowType: z.enum(["management_fee", "rent", "interest", "dividend", "loan_repayment", "service_fee", "other"]),
  description: z.string().nullable().optional(),
  annualAmount: z.string(),
  periodLabel: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true });

export const updateDealIntercompanyEliminationSchema = insertDealIntercompanyEliminationSchema.partial();
export const selectDealIntercompanyEliminationSchema = createSelectSchema(dealIntercompanyEliminations);
export type DealIntercompanyElimination = typeof dealIntercompanyEliminations.$inferSelect;
export type InsertDealIntercompanyElimination = z.infer<typeof insertDealIntercompanyEliminationSchema>;

// ============================================================================
// TABLE 5: Deal Income Inclusion
// ============================================================================

export const dealIncomeInclusion = pgTable("crm_x_deal_income_inclusion", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "set null" }),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "set null" }),
  sourceType: dealIncomeSourceTypeEnum("source_type").notNull(),
  sourceEntityId: uuid("source_entity_id"),
  sourceLabel: text("source_label").notNull(),
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull().default("0"),
  includeInCalculation: boolean("include_in_calculation").notNull().default(true),
  couplingOverrideReason: text("coupling_override_reason"),
  periodLabel: text("period_label"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  oneIncomeOwner: check(
    "chk_deal_income_inclusion_account_or_contact",
    sql`(${table.accountId} IS NULL) <> (${table.contactId} IS NULL)`,
  ),
}));

const baseInsertDealIncomeInclusionSchema = createInsertSchema(dealIncomeInclusion, {
  dealId: z.string().uuid(),
  accountId: z.string().uuid().nullable().optional(),
  contactId: z.string().uuid().nullable().optional(),
  sourceType: z.enum(["entity_ebitda", "guarantor_income", "rental_income", "dividend_income", "trust_distribution", "other_income"]),
  sourceEntityId: z.string().uuid().nullable().optional(),
  sourceLabel: z.string().min(1),
  amount: z.string().default("0"),
  includeInCalculation: z.boolean().default(true),
  couplingOverrideReason: z.string().nullable().optional(),
  periodLabel: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });

// M0 spec H1: every inclusion is coupled to exactly one owner (accountId XOR
// contactId). Enforced in Zod so a violation surfaces as a validated 400, never
// a DB-level 500 from chk_deal_income_inclusion_account_or_contact.
export const insertDealIncomeInclusionSchema = baseInsertDealIncomeInclusionSchema.refine(
  (d) => (d.accountId == null) !== (d.contactId == null),
  { message: "Provide exactly one owner: accountId XOR contactId", path: ["accountId"] },
);

export const updateDealIncomeInclusionSchema = baseInsertDealIncomeInclusionSchema.partial();
export const selectDealIncomeInclusionSchema = createSelectSchema(dealIncomeInclusion);
export type DealIncomeInclusion = typeof dealIncomeInclusion.$inferSelect;
export type InsertDealIncomeInclusion = z.infer<typeof insertDealIncomeInclusionSchema>;

// ============================================================================
// TABLE 6: Deal Commitment Inclusion
// ============================================================================

export const dealCommitmentInclusion = pgTable("crm_x_deal_commitment_inclusion", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "set null" }),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "set null" }),
  commitmentId: uuid("commitment_id").references(() => accountCommitments.id),
  commitmentLabel: text("commitment_label").notNull(),
  entityName: text("entity_name"),
  lenderName: text("lender_name"),
  annualAmount: numeric("annual_amount", { precision: 15, scale: 2 }).notNull().default("0"),
  overrideAmount: numeric("override_amount", { precision: 15, scale: 2 }),
  includeInCalculation: boolean("include_in_calculation").notNull().default(true),
  couplingOverrideReason: text("coupling_override_reason"),
  isProposed: boolean("is_proposed").default(false),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  oneCommitmentOwner: check(
    "chk_deal_commitment_inclusion_account_or_contact",
    sql`(${table.accountId} IS NULL) <> (${table.contactId} IS NULL)`,
  ),
}));

const baseInsertDealCommitmentInclusionSchema = createInsertSchema(dealCommitmentInclusion, {
  dealId: z.string().uuid(),
  accountId: z.string().uuid().nullable().optional(),
  contactId: z.string().uuid().nullable().optional(),
  commitmentId: z.string().uuid().nullable().optional(),
  commitmentLabel: z.string().min(1),
  entityName: z.string().nullable().optional(),
  lenderName: z.string().nullable().optional(),
  annualAmount: z.string().default("0"),
  overrideAmount: z.string().nullable().optional(),
  includeInCalculation: z.boolean().default(true),
  couplingOverrideReason: z.string().nullable().optional(),
  isProposed: z.boolean().default(false),
  notes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });

// M0 spec H1: every inclusion is coupled to exactly one owner (accountId XOR
// contactId). Enforced in Zod so a violation surfaces as a validated 400, never
// a DB-level 500 from chk_deal_commitment_inclusion_account_or_contact.
export const insertDealCommitmentInclusionSchema = baseInsertDealCommitmentInclusionSchema.refine(
  (d) => (d.accountId == null) !== (d.contactId == null),
  { message: "Provide exactly one owner: accountId XOR contactId", path: ["accountId"] },
);

export const updateDealCommitmentInclusionSchema = baseInsertDealCommitmentInclusionSchema.partial();
export const selectDealCommitmentInclusionSchema = createSelectSchema(dealCommitmentInclusion);
export type DealCommitmentInclusion = typeof dealCommitmentInclusion.$inferSelect;
export type InsertDealCommitmentInclusion = z.infer<typeof insertDealCommitmentInclusionSchema>;

// ============================================================================
// TABLE 7: Deal Consolidated Snapshot
// ============================================================================

export const dealConsolidatedSnapshot = pgTable("crm_x_deal_consolidated_snapshot", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  snapshotDate: timestamp("snapshot_date", { withTimezone: true }).notNull().defaultNow(),
  periodLabel: text("period_label").notNull(),
  totalEntityRevenue: numeric("total_entity_revenue", { precision: 15, scale: 2 }).default("0"),
  totalEntityEbitda: numeric("total_entity_ebitda", { precision: 15, scale: 2 }).default("0"),
  totalEliminations: numeric("total_eliminations", { precision: 15, scale: 2 }).default("0"),
  totalGuarantorIncome: numeric("total_guarantor_income", { precision: 15, scale: 2 }).default("0"),
  totalAdjustments: numeric("total_adjustments", { precision: 15, scale: 2 }).default("0"),
  adjustedEbitda: numeric("adjusted_ebitda", { precision: 15, scale: 2 }).default("0"),
  totalAssets: numeric("total_assets", { precision: 15, scale: 2 }).default("0"),
  totalLiabilities: numeric("total_liabilities", { precision: 15, scale: 2 }).default("0"),
  netAssets: numeric("net_assets", { precision: 15, scale: 2 }).default("0"),
  totalIncome: numeric("total_income", { precision: 15, scale: 2 }).default("0"),
  totalCommitments: numeric("total_commitments", { precision: 15, scale: 2 }).default("0"),
  dscr: numeric("dscr", { precision: 10, scale: 4 }),
  icr: numeric("icr", { precision: 10, scale: 4 }),
  surplus: numeric("surplus", { precision: 15, scale: 2 }).default("0"),
  passesServiceability: boolean("passes_serviceability"),
  calcVersion: text("calc_version"),
  inputDigest: text("input_digest"),
  canonicalInputs: jsonb("canonical_inputs"),
  canonicalOutputs: jsonb("canonical_outputs"),
  baseVersion: integer("base_version"),
  isStale: boolean("is_stale").notNull().default(false),
  containsLegacyData: boolean("contains_legacy_data").notNull().default(false),
  legacyAcknowledgedBy: uuid("legacy_acknowledged_by"),
  eliminationsDisabled: boolean("eliminations_disabled").notNull().default(false),
  entitiesIncluded: integer("entities_included").default(0),
  guarantorsIncluded: integer("guarantors_included").default(0),
  calculationNotes: text("calculation_notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  createdBy: uuid("created_by"),
});

export const insertDealConsolidatedSnapshotSchema = createInsertSchema(dealConsolidatedSnapshot, {
  dealId: z.string().uuid(),
  periodLabel: z.string().min(1),
  totalEntityRevenue: z.string().nullable().optional(),
  totalEntityEbitda: z.string().nullable().optional(),
  totalEliminations: z.string().nullable().optional(),
  totalGuarantorIncome: z.string().nullable().optional(),
  totalAdjustments: z.string().nullable().optional(),
  adjustedEbitda: z.string().nullable().optional(),
  totalAssets: z.string().nullable().optional(),
  totalLiabilities: z.string().nullable().optional(),
  netAssets: z.string().nullable().optional(),
  totalIncome: z.string().nullable().optional(),
  totalCommitments: z.string().nullable().optional(),
  dscr: z.string().nullable().optional(),
  icr: z.string().nullable().optional(),
  surplus: z.string().nullable().optional(),
  passesServiceability: z.boolean().nullable().optional(),
  entitiesIncluded: z.number().int().nullable().optional(),
  guarantorsIncluded: z.number().int().nullable().optional(),
  calculationNotes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true, createdBy: true, snapshotDate: true });

export const updateDealConsolidatedSnapshotSchema = insertDealConsolidatedSnapshotSchema.partial();
export const selectDealConsolidatedSnapshotSchema = createSelectSchema(dealConsolidatedSnapshot);
export type DealConsolidatedSnapshot = typeof dealConsolidatedSnapshot.$inferSelect;
export type InsertDealConsolidatedSnapshot = z.infer<typeof insertDealConsolidatedSnapshotSchema>;

// ============================================================================
// TABLE 8: HEM Benchmarks
// ============================================================================

export const hemBenchmarks = pgTable("crm_x_hem_benchmarks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  householdType: text("household_type").notNull(),
  locationCategory: text("location_category").notNull().default("metropolitan"),
  dependants: integer("dependants").notNull().default(0),
  annualAmount: numeric("annual_amount", { precision: 15, scale: 2 }).notNull(),
  monthlyAmount: numeric("monthly_amount", { precision: 15, scale: 2 }).notNull(),
  effectiveDate: date("effective_date").notNull(),
  expiryDate: date("expiry_date"),
  source: text("source").default("Melbourne Institute"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const insertHemBenchmarkSchema = createInsertSchema(hemBenchmarks, {
  householdType: z.string().min(1),
  locationCategory: z.string().default("metropolitan"),
  dependants: z.number().int().default(0),
  annualAmount: z.string(),
  monthlyAmount: z.string(),
  effectiveDate: z.string(),
  expiryDate: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true });

export const updateHemBenchmarkSchema = insertHemBenchmarkSchema.partial();
export const selectHemBenchmarkSchema = createSelectSchema(hemBenchmarks);
export type HemBenchmark = typeof hemBenchmarks.$inferSelect;
export type InsertHemBenchmark = z.infer<typeof insertHemBenchmarkSchema>;

// ============================================================================
// TABLE 9: Deal Serviceability Snapshots (GOLDEN-PATH F003)
// ============================================================================
// Stores the output of each CalculationOrchestrationService.recalculate() call.
// Data source for lender matching (F004) and credit memo auto-population (F005).

export const dealServiceabilitySnapshots = pgTable("crm_x_deal_serviceability_snapshots", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  dscr: numeric("dscr", { precision: 8, scale: 4 }),
  icr: numeric("icr", { precision: 8, scale: 4 }),
  lvr: numeric("lvr", { precision: 8, scale: 4 }),
  totalDebt: numeric("total_debt", { precision: 15, scale: 2 }),
  annualDebtService: numeric("annual_debt_service", { precision: 15, scale: 2 }),
  ebitda: numeric("ebitda", { precision: 15, scale: 2 }),
  interestExpense: numeric("interest_expense", { precision: 15, scale: 2 }),
  loanAmount: numeric("loan_amount", { precision: 15, scale: 2 }),
  securityValue: numeric("security_value", { precision: 15, scale: 2 }),
  dataSource: varchar("data_source", { length: 30 }).notNull().default("insufficient_data"),
  warnings: jsonb("warnings").notNull().default(sql`'[]'::jsonb`),
  calcVersion: text("calc_version"),
  inputDigest: text("input_digest"),
  canonicalInputs: jsonb("canonical_inputs"),
  canonicalOutputs: jsonb("canonical_outputs"),
  baseVersion: integer("base_version"),
  isStale: boolean("is_stale").notNull().default(false),
  containsLegacyData: boolean("contains_legacy_data").notNull().default(false),
  legacyAcknowledgedBy: uuid("legacy_acknowledged_by"),
  eliminationsDisabled: boolean("eliminations_disabled").notNull().default(false),
  calculatedAt: timestamp("calculated_at", { withTimezone: true }).notNull().defaultNow(),
  organisationId: uuid("organisation_id"),
  createdBy: uuid("created_by"),
});

export const insertDealServiceabilitySnapshotSchema = createInsertSchema(dealServiceabilitySnapshots, {
  dealId: z.string().uuid(),
  dscr: z.string().nullable().optional(),
  icr: z.string().nullable().optional(),
  lvr: z.string().nullable().optional(),
  totalDebt: z.string().nullable().optional(),
  annualDebtService: z.string().nullable().optional(),
  ebitda: z.string().nullable().optional(),
  interestExpense: z.string().nullable().optional(),
  loanAmount: z.string().nullable().optional(),
  securityValue: z.string().nullable().optional(),
  dataSource: z.enum(["consolidated", "single_entity", "insufficient_data"]).default("insufficient_data"),
  warnings: z.array(z.string()).default([]),
  organisationId: z.string().uuid().nullable().optional(),
  createdBy: z.string().uuid().nullable().optional(),
}).omit({ id: true, calculatedAt: true });

export const selectDealServiceabilitySnapshotSchema = createSelectSchema(dealServiceabilitySnapshots);
export type DealServiceabilitySnapshot = typeof dealServiceabilitySnapshots.$inferSelect;
export type InsertDealServiceabilitySnapshot = z.infer<typeof insertDealServiceabilitySnapshotSchema>;

// ============================================================================
// TABLE 10: Deal Lender Matches (GOLDEN-PATH F004)
// ============================================================================
// Persists the output of each LenderMatchingService.matchLenders() call.
// Full refresh on every POST — existing rows for the deal are deleted first.

import { lenderProducts } from "./schema-lenders.js";

export const dealLenderMatches = pgTable("crm_x_deal_lender_matches", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id")
    .notNull()
    .references(() => deals.id, { onDelete: "cascade" }),
  productId: uuid("product_id")
    .notNull()
    .references(() => lenderProducts.id, { onDelete: "cascade" }),
  serviceabilitySnapshotId: uuid("serviceability_snapshot_id").references(
    () => dealServiceabilitySnapshots.id,
  ),
  isEligible: boolean("is_eligible").notNull(),
  matchScore: numeric("match_score", { precision: 5, scale: 2 }),
  matchReasons: jsonb("match_reasons").notNull().default(sql`'[]'::jsonb`),
  failReasons: jsonb("fail_reasons").notNull().default(sql`'[]'::jsonb`),
  matchedAt: timestamp("matched_at", { withTimezone: true }).notNull().defaultNow(),
  organisationId: uuid("organisation_id"),
});

export const insertDealLenderMatchSchema = createInsertSchema(dealLenderMatches, {
  dealId: z.string().uuid(),
  productId: z.string().uuid(),
  serviceabilitySnapshotId: z.string().uuid().nullable().optional(),
  isEligible: z.boolean(),
  matchScore: z.string().nullable().optional(),
  matchReasons: z.array(z.string()).default([]),
  failReasons: z.array(z.string()).default([]),
  organisationId: z.string().uuid().nullable().optional(),
}).omit({ id: true, matchedAt: true });

export const selectDealLenderMatchSchema = createSelectSchema(dealLenderMatches);
export type DealLenderMatch = typeof dealLenderMatches.$inferSelect;
export type InsertDealLenderMatch = z.infer<typeof insertDealLenderMatchSchema>;

// ServiceabilityResult: the shape returned by the API and by CalculationOrchestrationService
export interface ServiceabilityResult {
  dealId: string;
  snapshotId: string;
  calculatedAt: string;
  dscr: number | null;
  icr: number | null;
  lvr: number | null;
  totalDebt: number | null;
  annualDebtService: number | null;
  ebitda: number | null;
  interestExpense: number | null;
  loanAmount: number | null;
  securityValue: number | null;
  dataSource: "consolidated" | "single_entity" | "insufficient_data";
  warnings: string[];
}
